import React, { type ReactElement, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { familyContentLoadRequest } from 'reactApp/modules/familyContent/familyContent.module';
import { getCurrentFamilyContentCategory, getFamilyContentAlbumsList } from 'reactApp/modules/familyContent/familyContent.selectors';
import { type FamilyContentAlbum, FamilyContentCategoryType } from 'reactApp/modules/familyContent/familyContent.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { Album } from 'reactApp/ui/Album/Album';
import { AlbumsLayout } from 'reactApp/ui/AlbumsLayout/AlbumsLayout';
import { CreateAlbum } from 'reactApp/ui/CreateAlbum/CreateAlbum';
import { DataList } from 'reactApp/ui/Datalist/DataList';
import { FamilyContentEmpty } from 'reactApp/ui/FamilyContentEmpty/FamilyContentEmpty';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { noop } from 'reactApp/utils/helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './FamilyContentPage.css';

interface FamilyContentPageProps {
    type: FamilyContentCategoryType;
    initialItemId?: string;
}

const createAlbumText = 'Создать семейный\nальбом';

const getXraySource = (type: FamilyContentCategoryType) => {
    switch (type) {
        case FamilyContentCategoryType.albums:
            return 'family-albums';
        case FamilyContentCategoryType.folders:
        default:
            return 'family-folders';
    }
};

const useMountHook = (type: FamilyContentCategoryType) => {
    const dispatch = useDispatch();

    const xraySource = getXraySource(type);

    useEffect(() => {
        dispatch(familyContentLoadRequest({ category: FamilyContentCategoryType[type] }));

        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'page-entered', source: xraySource });
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: xraySource, source: xraySource });
    }, [dispatch, type]);
};

// eslint-disable-next-line react/display-name
const FamilyFolderContent: React.FC<{ initialItemId: string }> = memo(
    ({ initialItemId }): ReactElement => (
        <div className={styles.folders} data-qa-id="family-datalist">
            <DataList goTo={noop} initialItemId={initialItemId} />
        </div>
    )
);

// eslint-disable-next-line react/display-name
const FamilyAlbumContent = memo((): ReactElement => {
    const customAlbums = useSelector(getFamilyContentAlbumsList);

    const onCreateFamilyAlbum = (): void => {
        toolbarActions.create('familyAlbum', 'emptyState', undefined, '', true, true);
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'album-click', type_album: 'family-album' });
    };

    /** CLOUD-4600 Это временное решение, чтобы открывать блядские паблики
        не через публичную ссылку, а по uuid потом будет на нормальной схеме через Link
    */
    const onAlbumClick = (event, album: FamilyContentAlbum) => {
        const { href, isOpenBlank } = album;
        if (isOpenBlank) {
            event.preventDefault();
            window.open(href, '_self');
        }
    };

    return (
        <div className={styles.albums} data-qa-id="family-album-list">
            <AlbumsLayout>
                <div>
                    <CreateAlbum onClick={onCreateFamilyAlbum} text={createAlbumText} data-qa-id="family-album-create" />
                </div>
                {customAlbums.map((album, id) => (
                    <Album
                        data-qa-id={`family-album-${id}`}
                        key={album.id}
                        {...album}
                        onClick={(event) => onAlbumClick(event, album as FamilyContentAlbum)}
                    />
                ))}
            </AlbumsLayout>
            <div className={styles.extraBlock}>
                <Footer storage={EStorageType.familyAlbums} />
            </div>
        </div>
    );
});

// eslint-disable-next-line react/display-name
export const FamilyContentPage: React.FC<FamilyContentPageProps> = memo(({ type, initialItemId = '' }): ReactElement => {
    const familyContentCategory = useSelector(getCurrentFamilyContentCategory);
    const storage = type === FamilyContentCategoryType.folders ? EStorageType.familyFolders : EStorageType.familyAlbums;

    useMountHook(type);

    if (familyContentCategory?.isLoading) {
        return <Spinner />;
    }

    if (familyContentCategory?.isLoaded && !familyContentCategory.childs.length) {
        return (
            <>
                <FamilyContentEmpty />
                <Footer storage={storage} isAbsolutePos />
            </>
        );
    }

    switch (type) {
        case FamilyContentCategoryType.albums:
            return <FamilyAlbumContent />;
        case FamilyContentCategoryType.folders:
        default:
            return <FamilyFolderContent initialItemId={initialItemId} />;
    }
});
